/* eslint-disable react/jsx-key */
// Station
import React, { useState, useContext, useEffect, useMemo } from 'react'
import { dateCol, intCol, numCol, strCol } from '@integec/grid-tools/lib/cols'
import {
  getApiTargetInfo,
  getEnvConfig,
  getPermitActions,
  getUnits,
} from 'core'
import { connect } from 'react-redux'
import {
  getKindleIntegrationMapping,
  kindleMySqlGeneration,
  kindleMySqlSaveGeneration,
} from 'api/pgads.js'
import CommonScreen from 'admin/component/CommonScreen'
import { Button, DatePicker } from 'antd'

import * as ScreenPermissions from 'core/EventActions'
import { hasAccesPermits, isValidYearRange } from 'core/utils'

import {
  DatePickerContext,
  ErrorContext,
  SpinContext,
  UnitSelectionContext,
} from 'context'
import { saveDataMessage } from 'kindle'

const headers = [
  intCol({
    ident: 'idDpnlGenFuelActivity',
    display: 'Id',
    isKey: true,
    width: 150,
    sortOrder: 'asc',
  }),

  strCol({
    ident: 'portfolio',
    display: 'Portfolio',
    isFiltered: true,
    isEditable: false,
    width: 150,
    sortOrder: 'asc',
  }),
  dateCol({
    ident: 'activityDate',
    display: 'Activity Date',
    isFiltered: true,
    isRequired: true,
    width: 150,
    isEditable: false,
    sortOrder: 'asc',
  }),
  strCol({
    ident: 'hourEnding',
    display: 'Hour Ending',
    isFiltered: true,
    isEditable: false,
    sortOrder: 'asc',
  }),
  strCol({
    ident: 'powerPlant',
    display: 'Power Plant',
    isFiltered: true,
    isEditable: false,
    width: 150,
    sortOrder: 'asc',
  }),
  strCol({
    ident: 'unit',
    display: 'Unit',
    isFiltered: true,
    isEditable: false,
    width: 100,
    sortOrder: 'asc',
  }),
  strCol({
    ident: 'activity',
    display: 'Activity',
    isFiltered: true,
    isEditable: false,
    width: 150,
    sortOrder: 'asc',
  }),

  numCol({
    displayFormat: '0.0000',
    ident: 'amount',
    display: 'Amount',
    isFiltered: true,
    isEditable: false,
    width: 200,
    sortOrder: 'asc',
  }),
]

const Generation = ({
  title = 'Generation',
  units,
  apiTargetInfo,
  ...props
}) => {
  const { selectedDateRangePicker, setSelectedDateRagePicker } = useContext(
    DatePickerContext
  )
  const { setError } = useContext(ErrorContext)
  const { spinning, setSpinning } = useContext(SpinContext)
  const [integrationUnits, setIntegrationUnits] = useState([])
  const { selectedUnits } = useContext(UnitSelectionContext)
  useEffect(
    _ => {
      getKindleIntegrationMapping(apiTargetInfo).fork(
        setError,
        integrationMapping => {
          const unitIds = integrationMapping
            .filter(u => u.unitId)
            .filter(u => u.importType === 'GEN')
            .map(u => u.unitId)
          const newUnitList = units.filter(u => unitIds.includes(u.unitId))
          setIntegrationUnits(newUnitList)
        }
      )
    },
    [apiTargetInfo, setError, units]
  )

  const extraUnitSelectionProps = _ => ({
    limitedUnits: integrationUnits,
    units: integrationUnits,
    // itemToLabel: e => e.emktId,
  })

  const { authGodMode } = props.envConfig

  const actionPermits = props.actionPermits

  const kindleMySqlGenerationWritePermisiion = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode: authGodMode,
        actionPermits,
        eventActions: [ScreenPermissions.KINDLE_MYSQL_GENERATION_ADMIN],
        matchAny: true,
      }),
    [actionPermits, authGodMode]
  )

  const withCriteria = (_, _2, su) => {
    return {
      start: selectedDateRangePicker[0],
      end: selectedDateRangePicker[1],
      unitId: su.map(u => u.unitId),
    }
  }

  const screenCriteria = (s, p, u, onChange) => {
    return (
      <DatePicker.RangePicker
        style={{ marginLeft: '15px' }}
        allowClear={false}
        format="MM/DD/YYYY"
        value={selectedDateRangePicker}
        onChange={d => {
          setSelectedDateRagePicker(d)
          onChange()
          // retrieve({ dates: d })
        }}
      />
    )
  }

  const saveData = _ => {
    const unitIds = integrationUnits.map(u => u.unitId)
    // const newUnitList = units.filter(u => unitIds.includes(u.unitId))
    const unitId = selectedUnits
      .filter(u => unitIds.includes(u.unitId))
      .map(u => u.unitId)

    setSpinning(true)
    kindleMySqlSaveGeneration(apiTargetInfo)({
      start: selectedDateRangePicker[0],
      end: selectedDateRangePicker[1],
      unitId,
    }).fork(setError, _ => {
      setSpinning(false)
      // message.info('Data saved')
      saveDataMessage()
    })
  }

  const extraCriteriaAfterButton = _ => {
    if (kindleMySqlGenerationWritePermisiion)
      return (
        <Button type="danger" onClick={saveData} loading={spinning} icon="save">
          Save Remote Data
        </Button>
      )
    return <div />
  }

  const beforeRetrieveValidation = ({ validYearRange }) => {
    return isValidYearRange(
      validYearRange,
      selectedDateRangePicker[0],
      selectedDateRangePicker[1]
    )
  }

  return [
    <CommonScreen
      beforeRetrieveValidation={beforeRetrieveValidation}
      retrieveLabel="Retrieve Remote Data"
      withUnitSelection
      extraUnitSelectionProps={extraUnitSelectionProps}
      screenCriteria={screenCriteria}
      extraCriteriaAfterButton={extraCriteriaAfterButton}
      title={title}
      header={headers}
      retrieveTask={kindleMySqlGeneration}
      withCriteria={withCriteria}
      insertComp={false}
      insertCompHeight={90}
      heightOffset={270}
      allowInsert={false}
      isAdminScreen={false}
      withoutModify
      withDelete={false}
      isEditableGrid={_ => false}
      widthOffset={50}
      sortOptions={headers}
      initialSortOptions={headers}
      autoRetrieve={false}
    />,
  ]
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  envConfig: getEnvConfig(state),
  actionPermits: getPermitActions(state),
  units: getUnits(state),
})

export default connect(mapStateToProps)(Generation)
